import {
  ShoppingCart,
  Truck,
  PackageCheck,
  ReceiptText,
  Store,
  Users,
  LayoutDashboard,
  Building2,
  Boxes,
  BarChart3,
  Palette,
  Car,
  FolderOpen,
  HardDrive,
} from "lucide-react";

export const ORIGINE_LABELS = {
  mercato: "Dal mercato",
  fuori_mercato: "Fuori mercato",
};

export const COLOR_PRESETS = [
  { name: "Verde", value: "160 84% 39%" },
  { name: "Blu", value: "217 91% 60%" },
  { name: "Ambra", value: "38 92% 50%" },
  { name: "Viola", value: "271 81% 56%" },
  { name: "Rosso", value: "0 84% 60%" },
  { name: "Teal", value: "173 80% 40%" },
  { name: "Arancio", value: "24 95% 53%" },
];

export const ROLE_LABELS = {
  admin: "Amministratore",
  compra: "Compra",
  picker: "Picker",
  accettazione: "Accettazione",
  amministrazione: "Amministrazione",
  autista: "Autista",
};

export const ROLE_COLORS = {
  admin: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  compra: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  picker: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  accettazione: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  amministrazione: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  autista: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};

export const STATUS_LABELS = {
  da_ritirare: "Da ritirare",
  ritirato: "Ritirato",
  arrivato: "Arrivato",
};

export const STATUS_COLORS = {
  da_ritirare: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  ritirato: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  arrivato: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

// path, label, icon, module (permesso richiesto) o admin-only
export const MODULE_LABELS = {
  acquisti: "Acquisti",
  ritiri: "Ritiri",
  accettazione: "Accettazione",
  bolle: "Bolle",
  fornitori: "Fornitori",
  prodotti: "Prodotti",
  posteggi: "Posteggi",
  statistiche: "Statistiche",
  consegne: "Consegne",
  resi: "Resi",
};
export const MODULES = Object.keys(MODULE_LABELS);

export const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/acquisti", label: "Acquisti", icon: ShoppingCart, module: "acquisti" },
  { path: "/ritiri", label: "Ritiri", icon: Truck, module: "ritiri" },
  { path: "/accettazione", label: "Accettazione", icon: PackageCheck, module: "accettazione" },
  { path: "/bolle", label: "Bolle", icon: ReceiptText, module: "bolle" },
  { path: "/documenti", label: "Documenti", icon: FolderOpen, module: "bolle" },
  { path: "/fornitori", label: "Fornitori", icon: Building2, module: "fornitori" },
  { path: "/prodotti", label: "Prodotti", icon: Boxes, module: "prodotti" },
  { path: "/posteggi", label: "Posteggi", icon: Store, module: "posteggi" },
  { path: "/statistiche", label: "Statistiche", icon: BarChart3, module: "statistiche" },
  { path: "/autisti", label: "Autisti", icon: Car, modulesAny: ["consegne", "resi"] },
  { path: "/utenti", label: "Utenti", icon: Users, admin: true },
  { path: "/hd", label: "HD / Backup", icon: HardDrive, admin: true },
  { path: "/personalizza", label: "Personalizza", icon: Palette, admin: true },
];

export function canAccess(item, user) {
  if (!user) return false;
  if (item.admin) return !!user.is_admin || user.role === "admin";
  if (!item.module && !item.modulesAny) return true;
  if (user.is_admin || user.role === "admin") return true;
  if (item.modulesAny) return item.modulesAny.some((m) => !!user.permissions_effective?.[m]?.view);
  return !!user.permissions_effective?.[item.module]?.view;
}

// Testi personalizzabili dall'admin (sezione Personalizza)
export const TEXT_GROUPS = [
  {
    group: "Login e schermata d'accesso",
    items: [
      { key: "login_hero_title", label: "Titolo grande (hero)", def: "La tua distribuzione ortofrutta, sotto controllo.", multiline: true },
      { key: "login_hero_subtitle", label: "Sottotitolo hero", def: "Acquisti, ritiri, accettazione e bolle. Tutto il tuo team coordinato in un unico posto.", multiline: true },
      { key: "login_heading", label: "Titolo box accesso", def: "Accedi" },
      { key: "login_subtitle", label: "Sottotitolo accesso", def: "Entra con le credenziali del tuo team." },
    ],
  },
  {
    group: "Dashboard",
    items: [
      { key: "dashboard_subtitle", label: "Sottotitolo", def: "Ecco lo stato della giornata al mercato." },
    ],
  },
  {
    group: "Acquisti",
    items: [
      { key: "acquisti_title", label: "Titolo", def: "Acquisti" },
      { key: "acquisti_subtitle", label: "Sottotitolo", def: "Registra la merce comprata ai posteggi e assegna il ritiro." },
    ],
  },
  {
    group: "Ritiri",
    items: [
      { key: "ritiri_title", label: "Titolo", def: "I miei ritiri" },
      { key: "ritiri_subtitle", label: "Sottotitolo", def: "Ritira la merce dai posteggi e portala al banco." },
    ],
  },
  {
    group: "Accettazione",
    items: [
      { key: "accettazione_title", label: "Titolo", def: "Accettazione" },
      { key: "accettazione_subtitle", label: "Sottotitolo", def: "Conferma la merce che arriva al banco dai picker." },
    ],
  },
  {
    group: "Bolle",
    items: [
      { key: "bolle_title", label: "Titolo", def: "Bolle d'acquisto" },
      { key: "bolle_subtitle", label: "Sottotitolo", def: "Registra le bolle dei posteggi con prodotti e prezzi." },
    ],
  },
  {
    group: "Fornitori",
    items: [
      { key: "fornitori_title", label: "Titolo", def: "Fornitori" },
      { key: "fornitori_subtitle", label: "Sottotitolo", def: "Anagrafica fornitori del mercato e fuori mercato. Si popola anche in automatico dalle bolle." },
    ],
  },
  {
    group: "Prodotti",
    items: [
      { key: "prodotti_title", label: "Titolo", def: "Prodotti" },
      { key: "prodotti_subtitle", label: "Sottotitolo", def: "Catalogo articoli. Si aggiorna in automatico ad ogni bolla registrata (con ultimo prezzo)." },
    ],
  },
  {
    group: "Posteggi",
    items: [
      { key: "posteggi_title", label: "Titolo", def: "Posteggi" },
      { key: "posteggi_subtitle", label: "Sottotitolo", def: "Gestisci i posteggi e fornitori del mercato." },
    ],
  },
  {
    group: "Statistiche",
    items: [
      { key: "statistiche_title", label: "Titolo", def: "Statistiche" },
      { key: "statistiche_subtitle", label: "Sottotitolo", def: "Analisi professionale degli acquisti: spesa per fornitore, per articolo e andamento." },
    ],
  },
  {
    group: "Consegne",
    items: [
      { key: "consegne_title", label: "Titolo", def: "Consegne" },
      { key: "consegne_subtitle", label: "Sottotitolo", def: "Registra le consegne ai clienti con foto e note per la tracciabilità." },
    ],
  },
  {
    group: "Resi",
    items: [
      { key: "resi_title", label: "Titolo", def: "Resi" },
      { key: "resi_subtitle", label: "Sottotitolo", def: "Documenta i resi dei clienti con foto, prodotto e motivo." },
    ],
  },
  {
    group: "Team / Utenti",
    items: [
      { key: "utenti_title", label: "Titolo", def: "Team" },
      { key: "utenti_subtitle", label: "Sottotitolo", def: "Crea e gestisci gli account del tuo team." },
    ],
  },
];

export const TEXT_DEFAULTS = Object.fromEntries(
  TEXT_GROUPS.flatMap((g) => g.items.map((i) => [i.key, i.def]))
);
